import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import axios from "axios";
import api, { formatApiError, getAuthToken, API as API_BASE } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { toast } from "sonner";
import {
  CloudArrowUp, FileCsv, CheckCircle, WarningCircle, ArrowsClockwise,
  Eye, MagnifyingGlass, PencilSimple, Trash, Archive, ArrowCounterClockwise,
  DownloadSimple, ChartBar, FloppyDisk, X, PaperPlaneTilt,
} from "@phosphor-icons/react";

/**
 * Admin tile — Preloaded Trucks expansion-engine v1.1.
 *
 * Sections (top → bottom):
 *  1. City progress dashboard — preloaded / claimed / verified / active
 *     counts plus a "Launch readiness" % for the current city.
 *  2. CSV upload — file picker + dry-run Preview modal + real upload
 *     with progress bar. Idempotent by `(city_slug, slug)`. Reports
 *     inserted / updated / skipped / errors and offers `errors.csv`
 *     download for any rejected rows.
 *  3. Search + inline edit drawer — type-ahead search hitting the
 *     server, click row → drawer with editable fields, save back via
 *     `PUT /api/admin/preloaded-trucks/{id}`.
 *  4. Export — dropdown to download a CSV for `all | claimed |
 *     unclaimed | verified | pending` rows in this city.
 *  5. Bulk archive / hard-delete by city — confirmation-gated.
 *  6. Toggle to include archived rows + per-row restore button.
 */
const RESEARCH_LABELS = {
  needs_research: "Needs research",
  basic_complete: "Basic complete",
  media_complete: "Media complete",
  imported: "Imported",
  claimed: "Claimed",
  verified: "Verified",
};

const RESEARCH_OPTIONS = Object.keys(RESEARCH_LABELS);
const STATUS_OPTIONS = ["coming_soon", "unclaimed", "claimed", "active"];
const CLAIM_OPTIONS = ["unclaimed", "pending", "verified"];
const PRIORITY_OPTIONS = ["", "P0", "P1", "P2", "P3"];

export default function PreloadedTrucksImporterCard({ cityOverride, highlightSlug }) {
  // When `cityOverride` is provided (page-controlled mode used by
  // /admin/preloaded-trucks), the page owns the city and we hide the
  // internal city input entirely. When undefined (legacy toolbox use),
  // the card uses its own internal state. Keeping both modes lets us
  // ship the new page without breaking any deep-links to the toolbox.
  const controlled = !!cityOverride;
  const initialCity = controlled ? cityOverride : "austin";
  const [city, setCity] = useState(initialCity);
  const [activeCity, setActiveCity] = useState(initialCity);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [report, setReport] = useState(null);
  const [previewReport, setPreviewReport] = useState(null);

  const [rows, setRows] = useState([]);
  const [rowsLoading, setRowsLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [includeArchived, setIncludeArchived] = useState(false);

  const [editing, setEditing] = useState(null); // row being edited
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkConfirmText, setBulkConfirmText] = useState("");
  const [bulkArchiveMode, setBulkArchiveMode] = useState(true);
  const [bulkRunning, setBulkRunning] = useState(false);

  // Bulk-invite dialog state — separate from bulk archive/delete
  // because the two flows are unrelated (invite = outbound email;
  // bulk archive = destructive DB action) and mashing them together
  // would make both harder to reason about.
  const [bulkInviteOpen, setBulkInviteOpen] = useState(false);
  const [bulkInvitePreview, setBulkInvitePreview] = useState(null);
  const [bulkInvitePreviewLoading, setBulkInvitePreviewLoading] = useState(false);
  const [bulkInviteResend, setBulkInviteResend] = useState(false);
  const [bulkInviteRunning, setBulkInviteRunning] = useState(false);
  const [bulkInviteResult, setBulkInviteResult] = useState(null);

  const [stats, setStats] = useState({ trucks_preloaded: 0, trucks_claimed: 0, trucks_active: 0 });
  // Invite-funnel stats — populated from the admin-only
  // `/admin/preloaded-trucks/engagement-stats` endpoint. Null until
  // the first successful fetch so we can distinguish "not loaded yet"
  // from "zero engagement".
  const [engagement, setEngagement] = useState(null);

  const fileInputRef = useRef(null);

  const normalizedCity = (city || "").trim().toLowerCase();

  const loadRows = useCallback(async (citySlug, searchTerm, withArchived) => {
    const normalized = (citySlug || "").trim().toLowerCase();
    if (!normalized) return;
    setRowsLoading(true);
    try {
      const params = new URLSearchParams({ city: normalized });
      if (searchTerm && searchTerm.trim()) params.set("search", searchTerm.trim());
      if (withArchived) params.set("include_archived", "true");
      const { data } = await api.get(`/admin/preloaded-trucks?${params.toString()}`);
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't load preloaded trucks");
    } finally {
      setRowsLoading(false);
    }
  }, []);

  const loadStats = useCallback(async (citySlug) => {
    const normalized = (citySlug || "").trim().toLowerCase();
    if (!normalized) return;
    try {
      const { data } = await api.get(`/preloaded-trucks/stats?city=${encodeURIComponent(normalized)}`);
      setStats(data || {});
    } catch {
      /* non-fatal — the dashboard just shows zeros */
    }
    // Engagement stats are admin-only and independent of the public
    // stats endpoint — fetched in parallel and merged onto local state
    // so the sent/clicked/claimed tiles render alongside the existing
    // preload/claim/verify counts.
    try {
      const { data } = await api.get(
        `/admin/preloaded-trucks/engagement-stats?city=${encodeURIComponent(normalized)}`,
      );
      setEngagement(data || null);
    } catch {
      setEngagement(null);
    }
  }, []);

  // Initial load: defaults to "austin" but parent can override.
  // We don't re-fetch on every keystroke of the city input — the user
  // types the new city then clicks "Switch city".
  useEffect(() => {
    loadRows(initialCity, "", false);
    loadStats(initialCity);
  }, [loadRows, loadStats, initialCity]);

  // When the parent flips cityOverride (e.g. user picks a different
  // city in the page-level dropdown), refetch everything for that city.
  useEffect(() => {
    if (!controlled) return;
    setCity(cityOverride);
    setActiveCity(cityOverride);
    setSearch("");
    setReport(null);
    setPreviewReport(null);
    loadRows(cityOverride, "", false);
    loadStats(cityOverride);
  }, [cityOverride, controlled, loadRows, loadStats]);

  // Search is debounced — refetch from the server when user pauses typing.
  useEffect(() => {
    const t = setTimeout(() => {
      loadRows(activeCity, search, includeArchived);
    }, 250);
    return () => clearTimeout(t);
  }, [search, activeCity, includeArchived, loadRows]);

  // Deep-link support — when the URL contains ?highlight=<slug> (e.g.
  // from the admin claim-notification email), scroll to the matching
  // row and give it a temporary ring so the admin's eye lands on it.
  useEffect(() => {
    if (!highlightSlug) return;
    if (!rows.some((r) => r.slug === highlightSlug)) return;
    const el = document.querySelector(`[data-testid="preload-row-${highlightSlug}"]`);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [highlightSlug, rows]);

  const handlePickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/\.csv$/i.test(f.name)) {
      toast.error("Pick a .csv file");
      return;
    }
    setFile(f);
    setReport(null);
    setPreviewReport(null);
  };

  // Performs an axios POST with upload-progress (multipart). Shared
  // between Preview (dry_run) and the real commit so the UX is
  // identical for both.
  const submitImport = async ({ dryRun }) => {
    const form = new FormData();
    form.append("file", file);
    form.append("city", normalizedCity);
    form.append("dry_run", dryRun ? "true" : "false");
    const token = getAuthToken();
    setUploadProgress(0);
    const { data } = await axios.post(
      `${API_BASE}/admin/preloaded-trucks/import`,
      form,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        withCredentials: true,
        onUploadProgress: (e) => {
          if (!e.total) return;
          setUploadProgress(Math.round((e.loaded / e.total) * 100));
        },
      },
    );
    return data;
  };

  const handlePreview = async () => {
    if (!file) { toast.error("Choose a CSV file first"); return; }
    if (!normalizedCity) { toast.error("City is required"); return; }
    setUploading(true);
    setReport(null);
    try {
      const data = await submitImport({ dryRun: true });
      setPreviewReport(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Preview failed");
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleConfirmImport = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const data = await submitImport({ dryRun: false });
      setReport(data);
      setPreviewReport(null);
      const msg = `Imported · ${data.inserted} new, ${data.updated} updated, ${data.skipped} skipped`;
      if ((data.errors || []).length) {
        toast.warning(`${msg} (${data.errors.length} row error${data.errors.length === 1 ? "" : "s"})`);
      } else {
        toast.success(msg);
      }
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      setActiveCity(normalizedCity);
      setCity(normalizedCity);
      await Promise.all([
        loadRows(normalizedCity, search, includeArchived),
        loadStats(normalizedCity),
      ]);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleSwitchCity = async () => {
    setActiveCity(normalizedCity);
    await Promise.all([
      loadRows(normalizedCity, search, includeArchived),
      loadStats(normalizedCity),
    ]);
  };

  const downloadErrorsCsv = () => {
    const errs = (report?.errors || previewReport?.errors || []);
    if (!errs.length) return;
    const lines = ["row,reason", ...errs.map(e => `${e.row},"${(e.reason || "").replace(/"/g, '""')}"`)];
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `import-errors-${activeCity || "city"}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const handleExport = async (filter) => {
    try {
      const token = getAuthToken();
      const url = `${API_BASE}/admin/preloaded-trucks/export?city=${encodeURIComponent(activeCity)}&filter=${filter}`;
      // Use fetch so we can attach the token and still get the binary blob.
      const r = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
      });
      if (!r.ok) throw new Error(`Export failed (${r.status})`);
      const blob = await r.blob();
      const dl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = dl;
      a.download = `preloaded-trucks-${activeCity}-${filter}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(dl);
      toast.success(`Exported ${filter} rows for ${activeCity}`);
    } catch (e) {
      toast.error(e.message || "Export failed");
    }
  };

  const openEdit = (row) => {
    setEditing(row);
    setEditForm({
      truck_name: row.truck_name || "",
      cuisine_type: row.cuisine_type || "",
      short_description: row.short_description || "",
      website_url: row.website_url || "",
      instagram_url: row.instagram_url || "",
      facebook_url: row.facebook_url || "",
      tiktok_url: row.tiktok_url || "",
      email: row.email || "",
      phone: row.phone || "",
      google_maps_url: row.google_maps_url || "",
      yelp_url: row.yelp_url || "",
      food_truck_park: row.food_truck_park || "",
      service_area: row.service_area || "",
      logo_url: row.logo_url || "",
      status: row.status || "coming_soon",
      claim_status: row.claim_status || "unclaimed",
      research_status: row.research_status || "imported",
      priority_tier: row.priority_tier || "",
      opportunity_score: row.opportunity_score ?? "",
      source_notes: row.source_notes || "",
    });
  };

  const handleSaveEdit = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const payload = { ...editForm };
      // Empty strings → null on server but explicit blanks are fine.
      // Opportunity score may be empty (means "clear").
      if (payload.opportunity_score === "") payload.opportunity_score = null;
      else if (payload.opportunity_score != null) payload.opportunity_score = Number(payload.opportunity_score);
      const { data } = await api.put(`/admin/preloaded-trucks/${editing.id}`, payload);
      toast.success(`Saved ${data.truck_name}`);
      setEditing(null);
      await Promise.all([
        loadRows(activeCity, search, includeArchived),
        loadStats(activeCity),
      ]);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't save");
    } finally {
      setSaving(false);
    }
  };

  const handleRestore = async (rowId) => {
    try {
      await api.post(`/admin/preloaded-trucks/${rowId}/restore`);
      toast.success("Restored");
      await Promise.all([
        loadRows(activeCity, search, includeArchived),
        loadStats(activeCity),
      ]);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Restore failed");
    }
  };

  // Tracks which row id is currently sending so we can disable just that
  // row's button (vs. a global spinner that would block the whole UI
  // when an admin is firing 25 invites in quick succession).
  const [sendingInviteId, setSendingInviteId] = useState(null);

  const handleSendInvite = async (row) => {
    if (sendingInviteId) return;
    const alreadySent = !!row.claim_invite_sent_at;
    if (alreadySent) {
      const ok = window.confirm(
        `${row.truck_name} was already invited on ${new Date(row.claim_invite_sent_at).toLocaleString()}.\n\nSend another invite email to ${row.email || "the same address"}?`,
      );
      if (!ok) return;
    }
    setSendingInviteId(row.id);
    try {
      const { data } = await api.post(`/admin/preloaded-trucks/${row.id}/send-claim-invite`);
      toast.success(`Invite sent to ${data.email || row.email || "owner"}`, {
        description: `${row.truck_name} · count: ${data.claim_invite_send_count}`,
      });
      await loadRows(activeCity, search, includeArchived);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't send invite");
    } finally {
      setSendingInviteId(null);
    }
  };

  const handleBulkSubmit = async () => {
    if (bulkConfirmText.trim().toLowerCase() !== activeCity) {
      toast.error("Confirmation text must match the city slug exactly");
      return;
    }
    setBulkRunning(true);
    try {
      const params = new URLSearchParams({
        confirm_city: activeCity,
        archive: bulkArchiveMode ? "true" : "false",
      });
      const { data } = await api.delete(`/admin/preloaded-trucks/by-city/${activeCity}?${params.toString()}`);
      const verb = bulkArchiveMode ? "archived" : "deleted";
      const n = bulkArchiveMode ? data.archived : data.deleted;
      toast.success(`${n} row${n === 1 ? "" : "s"} ${verb} for ${activeCity}`);
      setBulkOpen(false);
      setBulkConfirmText("");
      await Promise.all([
        loadRows(activeCity, search, includeArchived),
        loadStats(activeCity),
      ]);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Bulk operation failed");
    } finally {
      setBulkRunning(false);
    }
  };

  // Bulk-invite: open dialog + fetch dry-run count so the confirm
  // step is honest about how many emails will actually go out.
  const openBulkInvite = async () => {
    setBulkInviteOpen(true);
    setBulkInviteResult(null);
    setBulkInviteResend(false);
    await fetchBulkInvitePreview(false);
  };

  const fetchBulkInvitePreview = async (resend) => {
    setBulkInvitePreviewLoading(true);
    try {
      const { data } = await api.get(
        `/admin/preloaded-trucks/bulk-invite-preview?city=${encodeURIComponent(activeCity)}&resend=${resend ? "true" : "false"}`,
      );
      setBulkInvitePreview(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't load invite preview");
      setBulkInvitePreview(null);
    } finally {
      setBulkInvitePreviewLoading(false);
    }
  };

  const handleBulkInviteResendToggle = async (next) => {
    setBulkInviteResend(next);
    await fetchBulkInvitePreview(next);
  };

  const handleBulkInviteSubmit = async () => {
    if (!bulkInvitePreview?.eligible) {
      toast.error("No eligible trucks to invite");
      return;
    }
    setBulkInviteRunning(true);
    try {
      const { data } = await api.post(
        `/admin/preloaded-trucks/bulk-send-claim-invites?city=${encodeURIComponent(activeCity)}&resend=${bulkInviteResend ? "true" : "false"}`,
      );
      setBulkInviteResult(data);
      if (data.failed > 0) {
        toast.warning(
          `Sent ${data.sent}/${data.attempted}, ${data.failed} failed — check email log`,
        );
      } else {
        toast.success(
          `Invited ${data.sent} truck${data.sent === 1 ? "" : "s"} in ${activeCity}${data.remaining ? ` · ${data.remaining} left in queue` : ""}`,
        );
      }
      // Refresh rows so the "Invited" pill updates on the table.
      await Promise.all([
        loadRows(activeCity, search, includeArchived),
        loadStats(activeCity),
      ]);
      // Also refresh the preview so the counts drop to 0 (or show
      // whatever's still in the queue after batch cap).
      await fetchBulkInvitePreview(bulkInviteResend);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Bulk invite failed");
    } finally {
      setBulkInviteRunning(false);
    }
  };

  const counts = useMemo(() => {
    const verified = rows.filter(r => r.claim_status === "verified").length;
    const pending = rows.filter(r => r.claim_status === "pending").length;
    const unclaimed = rows.filter(r => !r.claim_status || r.claim_status === "unclaimed").length;
    const archived = rows.filter(r => !!r.archived_at).length;
    return { verified, pending, unclaimed, archived };
  }, [rows]);

  const launchReadiness = useMemo(() => {
    const total = stats.trucks_preloaded || 0;
    if (!total) return 0;
    return Math.round(((stats.trucks_claimed || 0) / total) * 100);
  }, [stats]);

  return (
    <div className="space-y-6" data-testid="preloaded-trucks-importer">
      {/* 1. City progress dashboard */}
      <section className="rounded-xl border border-border bg-muted/20 p-4" data-testid="preload-dashboard">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <div className="font-bold text-sm inline-flex items-center gap-2">
            <ChartBar weight="duotone" className="h-4 w-4 text-primary" />
            City progress · <span className="text-primary uppercase">{activeCity || "—"}</span>
          </div>
          <div className="text-xs text-muted-foreground">
            Launch readiness: <span className="font-bold text-foreground">{launchReadiness}%</span>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-center">
          <DashTile label="Preloaded" value={stats.trucks_preloaded || 0} testId="dash-preloaded" />
          <DashTile label="Claimed" value={stats.trucks_claimed || 0} testId="dash-claimed" />
          <DashTile label="Verified" value={counts.verified} testId="dash-verified" />
          <DashTile label="Active" value={stats.trucks_active || 0} testId="dash-active" />
          <DashTile label="Archived" value={counts.archived} testId="dash-archived" subtle />
        </div>

        {/* Invite funnel — hidden until at least one invite has been
            sent so we don't clutter the dashboard for cities that
            haven't started outreach yet. */}
        {engagement && engagement.invited > 0 && (
          <div className="mt-3 pt-3 border-t border-border" data-testid="preload-engagement-panel">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-muted-foreground inline-flex items-center gap-1.5">
                <PaperPlaneTilt weight="duotone" className="h-3 w-3 text-primary" />
                Invite funnel
              </div>
              <div className="text-[10px] text-muted-foreground">
                {engagement.click_to_claim_rate_pct != null && (
                  <>Click → claim: <span className="font-semibold text-foreground">{engagement.click_to_claim_rate_pct}%</span></>
                )}
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2 text-center">
              <DashTile
                label="Invited"
                value={engagement.invited}
                testId="engagement-invited"
              />
              <DashTile
                label={`Clicked${engagement.click_rate_pct != null ? ` · ${engagement.click_rate_pct}%` : ""}`}
                value={engagement.clicked}
                testId="engagement-clicked"
              />
              <DashTile
                label={`Claimed${engagement.claim_rate_pct != null ? ` · ${engagement.claim_rate_pct}%` : ""}`}
                value={engagement.claimed}
                testId="engagement-claimed"
              />
            </div>
          </div>
        )}
      </section>

      {/* 2. CSV format reference */}
      <div className="rounded-lg border border-border bg-muted/30 p-4 text-xs text-muted-foreground space-y-2">
        <div className="font-bold text-foreground text-sm">CSV format</div>
        <div>
          Header row required. Recognised columns:&nbsp;
          <code className="font-mono">truck_name</code> (required),&nbsp;
          <code className="font-mono">slug</code>, <code className="font-mono">city</code>,&nbsp;
          <code className="font-mono">state</code>, <code className="font-mono">cuisine_type</code>,&nbsp;
          <code className="font-mono">short_description</code>, <code className="font-mono">website_url</code>,&nbsp;
          <code className="font-mono">instagram_url</code>, <code className="font-mono">facebook_url</code>,&nbsp;
          <code className="font-mono">tiktok_url</code>, <code className="font-mono">email</code>,&nbsp;
          <code className="font-mono">phone</code>, <code className="font-mono">google_maps_url</code>,&nbsp;
          <code className="font-mono">yelp_url</code>, <code className="font-mono">food_truck_park</code>,&nbsp;
          <code className="font-mono">service_area</code>, <code className="font-mono">logo_url</code>,&nbsp;
          <code className="font-mono">status</code>&nbsp;(<em>coming_soon | unclaimed | claimed | active</em>),&nbsp;
          <code className="font-mono">claim_status</code>&nbsp;(<em>unclaimed | pending | verified</em>),&nbsp;
          <code className="font-mono">research_status</code>&nbsp;(<em>needs_research | basic_complete | media_complete | imported | claimed | verified</em>),&nbsp;
          <code className="font-mono">priority_tier</code>&nbsp;(<em>P0 | P1 | P2 | P3</em>),&nbsp;
          <code className="font-mono">opportunity_score</code>, <code className="font-mono">source_notes</code>.
        </div>
        <div className="italic">
          Idempotent: re-running the same CSV updates existing rows by&nbsp;
          <code className="font-mono">(city, slug)</code>. Nothing is ever deleted by import.
          Exports omit archive timestamps by design — re-importing an exported CSV will&nbsp;
          <strong>not</strong> restore archive state.
        </div>
      </div>

      {/* 3. City + file picker + Preview / Upload */}
      <div className={`grid grid-cols-1 gap-3 items-end ${controlled ? "sm:grid-cols-[1fr_auto]" : "sm:grid-cols-[160px_1fr_auto]"}`}>
        {!controlled && (
          <div>
            <Label htmlFor="preload-city" className="text-xs">City slug</Label>
            <div className="flex gap-1">
              <Input
                id="preload-city"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="austin"
                data-testid="preload-city-input"
              />
              {normalizedCity !== activeCity && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleSwitchCity}
                  className="h-10 px-2"
                  data-testid="preload-switch-city-btn"
                  title="Switch dashboard / table to this city"
                >
                  ↩
                </Button>
              )}
            </div>
          </div>
        )}
        <div>
          <Label htmlFor="preload-file" className="text-xs">CSV file</Label>
          <Input
            id="preload-file"
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            onChange={handlePickFile}
            data-testid="preload-file-input"
          />
        </div>
        <Button
          onClick={handlePreview}
          disabled={uploading || !file}
          className="h-10"
          data-testid="preload-preview-btn"
        >
          <CloudArrowUp weight="bold" className="h-4 w-4 mr-1.5" />
          {uploading ? "Uploading…" : "Preview import"}
        </Button>
      </div>

      {file && !uploading && !previewReport && (
        <div className="text-xs text-muted-foreground inline-flex items-center gap-1.5">
          <FileCsv weight="duotone" className="h-4 w-4" />
          Ready to preview: <span className="font-medium text-foreground">{file.name}</span> ({(file.size / 1024).toFixed(1)} KB)
        </div>
      )}

      {uploading && (
        <div className="space-y-1" data-testid="preload-progress">
          <div className="text-xs text-muted-foreground">Uploading… {uploadProgress}%</div>
          <div className="h-2 rounded-full bg-muted overflow-hidden">
            <div
              className="h-full bg-primary transition-[width] duration-150"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
        </div>
      )}

      {/* 4. Preview modal — confirm-or-cancel commit */}
      {previewReport && !uploading && (
        <PreviewPanel
          report={previewReport}
          onConfirm={handleConfirmImport}
          onCancel={() => setPreviewReport(null)}
          onDownloadErrors={downloadErrorsCsv}
        />
      )}

      {/* 5. Post-import results */}
      {report && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-4 space-y-2" data-testid="preload-report">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="font-bold text-sm text-emerald-900 inline-flex items-center gap-1.5">
              <CheckCircle weight="fill" className="h-4 w-4" /> Import complete
            </div>
            {(report.errors || []).length > 0 && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={downloadErrorsCsv}
                data-testid="preload-download-errors-btn"
              >
                <DownloadSimple weight="bold" className="h-3.5 w-3.5 mr-1" />
                Download errors.csv
              </Button>
            )}
          </div>
          <div className="text-xs text-emerald-900 grid grid-cols-3 gap-2">
            <div><span className="font-bold">{report.inserted}</span> inserted</div>
            <div><span className="font-bold">{report.updated}</span> updated</div>
            <div><span className="font-bold">{report.skipped}</span> skipped</div>
          </div>
          {(report.errors || []).length > 0 && (
            <div className="text-xs text-amber-900 space-y-1 pt-2 border-t border-emerald-200">
              <div className="font-bold inline-flex items-center gap-1.5">
                <WarningCircle weight="fill" className="h-4 w-4 text-amber-600" />
                {report.errors.length} row error{report.errors.length === 1 ? "" : "s"}
              </div>
              <ul className="list-disc pl-5 space-y-0.5">
                {report.errors.slice(0, 10).map((e, i) => (
                  <li key={i}>Row {e.row}: {e.reason}</li>
                ))}
              </ul>
              {report.errors.length > 10 && (
                <div className="italic">…+{report.errors.length - 10} more — download errors.csv for the full list.</div>
              )}
            </div>
          )}
        </div>
      )}

      {/* 6. Search + table actions */}
      <div className="pt-4 border-t border-border space-y-3">
        <div className="flex items-end gap-2 flex-wrap">
          <div className="flex-1 min-w-[200px]">
            <Label htmlFor="preload-search" className="text-xs">Search · truck name or slug</Label>
            <div className="relative">
              <MagnifyingGlass weight="bold" className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
              <Input
                id="preload-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="atx-tacos…"
                className="pl-9"
                data-testid="preload-search-input"
              />
            </div>
          </div>
          <label className="inline-flex items-center gap-2 text-xs text-muted-foreground pb-2 cursor-pointer">
            <input
              type="checkbox"
              checked={includeArchived}
              onChange={(e) => setIncludeArchived(e.target.checked)}
              data-testid="preload-include-archived"
            />
            Show archived
          </label>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => loadRows(activeCity, search, includeArchived)}
            disabled={rowsLoading}
            data-testid="preload-refresh-btn"
          >
            <ArrowsClockwise weight="bold" className={`h-4 w-4 mr-1 ${rowsLoading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>

        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="text-xs text-muted-foreground">
            {rows.length} row{rows.length === 1 ? "" : "s"} · {counts.verified} verified · {counts.pending} pending · {counts.unclaimed} unclaimed
            {includeArchived && counts.archived > 0 && (
              <> · {counts.archived} archived</>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <ExportMenu onExport={handleExport} />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={openBulkInvite}
              disabled={!rows.length}
              data-testid="preload-bulk-invite-btn"
            >
              <PaperPlaneTilt weight="bold" className="h-3.5 w-3.5 mr-1" />
              Send bulk invites
            </Button>
            <Button
              type="button"
              variant="destructive"
              size="sm"
              onClick={() => { setBulkOpen(true); setBulkConfirmText(""); setBulkArchiveMode(true); }}
              disabled={!rows.length}
              data-testid="preload-bulk-btn"
            >
              <Trash weight="bold" className="h-3.5 w-3.5 mr-1" />
              Bulk archive / delete
            </Button>
          </div>
        </div>

        {/* Table */}
        {rows.length === 0 ? (
          rowsLoading ? (
            <div className="text-xs text-muted-foreground italic px-3 py-6 text-center border border-dashed border-border rounded-lg">
              Loading…
            </div>
          ) : search ? (
            <div className="text-xs text-muted-foreground italic px-3 py-6 text-center border border-dashed border-border rounded-lg">
              No matches for &quot;{search}&quot; in &quot;{activeCity}&quot;.
            </div>
          ) : (
            <div
              className="rounded-xl border-2 border-dashed border-border bg-muted/20 px-6 py-12 text-center space-y-3"
              data-testid="preload-empty-state"
            >
              <div className="mx-auto w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <CloudArrowUp weight="duotone" className="h-6 w-6 text-primary" />
              </div>
              <div className="text-base font-bold text-foreground">
                No trucks have been preloaded for {activeCity.charAt(0).toUpperCase() + activeCity.slice(1)} yet.
              </div>
              <div className="text-xs text-muted-foreground max-w-sm mx-auto">
                Upload a CSV above to start creating preloaded truck profiles for this market.
                Rows are idempotent — re-uploading the same file is safe.
              </div>
              <Button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                data-testid="preload-empty-upload-btn"
              >
                <CloudArrowUp weight="bold" className="h-4 w-4 mr-1.5" />
                Upload CSV
              </Button>
            </div>
          )
        ) : (
          <div className="rounded-lg border border-border overflow-hidden">
            <div className="max-h-96 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-muted/40 sticky top-0 z-10">
                  <tr className="text-left">
                    <th className="px-3 py-2 font-semibold">Truck</th>
                    <th className="px-3 py-2 font-semibold">Tier</th>
                    <th className="px-3 py-2 font-semibold">Status</th>
                    <th className="px-3 py-2 font-semibold">Claim</th>
                    <th className="px-3 py-2 font-semibold">Research</th>
                    <th className="px-3 py-2 font-semibold">Email?</th>
                    <th className="px-3 py-2 font-semibold text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const profileHref = `/food-trucks/${r.city_slug || activeCity}/${r.slug}`;
                    const archived = !!r.archived_at;
                    const isHighlighted = !!highlightSlug && r.slug === highlightSlug;
                    return (
                      <tr
                        key={r.id}
                        className={`border-t border-border ${archived ? "bg-amber-50/40 text-muted-foreground" : ""} ${isHighlighted ? "ring-2 ring-orange-400 ring-inset bg-orange-50 dark:bg-orange-950/20" : ""}`}
                        data-testid={`preload-row-${r.slug}`}
                      >
                        <td className="px-3 py-2">
                          <div className="font-medium text-foreground inline-flex items-center gap-1.5">
                            {r.truck_name}
                            {archived && (
                              <span className="text-[9px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">
                                archived
                              </span>
                            )}
                            {!archived && r.claim_invite_sent_at && (
                              <span
                                className="text-[9px] font-bold uppercase tracking-wider bg-indigo-100 text-indigo-800 px-1.5 py-0.5 rounded"
                                title={`Invite sent ${new Date(r.claim_invite_sent_at).toLocaleString()} · ${r.claim_invite_send_count || 0}x`}
                              >
                                invited{(r.claim_invite_send_count || 0) > 1 ? ` ×${r.claim_invite_send_count}` : ""}
                              </span>
                            )}
                            {!archived && r.claim_invite_clicked_at && (
                              <span
                                className="text-[9px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded"
                                title={`First click ${new Date(r.claim_invite_clicked_at).toLocaleString()} · ${r.claim_invite_click_count || 0}x total`}
                                data-testid={`clicked-pill-${r.id}`}
                              >
                                clicked{(r.claim_invite_click_count || 0) > 1 ? ` ×${r.claim_invite_click_count}` : ""}
                              </span>
                            )}
                          </div>
                          {r.cuisine_type && <div className="text-muted-foreground text-[11px]">{r.cuisine_type}</div>}
                        </td>
                        <td className="px-3 py-2">{r.priority_tier || "—"}</td>
                        <td className="px-3 py-2">{r.status || "—"}</td>
                        <td className="px-3 py-2">
                          {r.claim_status === "verified" && (
                            <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold">
                              <CheckCircle weight="fill" className="h-3.5 w-3.5" /> verified
                            </span>
                          )}
                          {r.claim_status === "pending" && (
                            <span className="text-amber-700 font-semibold">pending</span>
                          )}
                          {(!r.claim_status || r.claim_status === "unclaimed") && (
                            <span className="text-muted-foreground">unclaimed</span>
                          )}
                        </td>
                        <td className="px-3 py-2">{RESEARCH_LABELS[r.research_status] || r.research_status || "—"}</td>
                        <td className="px-3 py-2">{r.has_email_on_file ? "yes" : "no"}</td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">
                          {archived ? (
                            <button
                              type="button"
                              onClick={() => handleRestore(r.id)}
                              className="inline-flex items-center gap-1 text-emerald-700 hover:underline mr-2"
                              data-testid={`preload-restore-${r.slug}`}
                            >
                              <ArrowCounterClockwise weight="bold" className="h-3.5 w-3.5" /> Restore
                            </button>
                          ) : (
                            <>
                              {r.has_email_on_file && r.claim_status !== "verified" && (
                                <button
                                  type="button"
                                  onClick={() => handleSendInvite(r)}
                                  disabled={sendingInviteId === r.id}
                                  className="inline-flex items-center gap-1 text-indigo-700 hover:underline mr-2 disabled:opacity-50"
                                  title={
                                    r.claim_invite_sent_at
                                      ? `Last sent ${new Date(r.claim_invite_sent_at).toLocaleString()} (count: ${r.claim_invite_send_count || 0})`
                                      : "Send a one-click claim invite email to this owner"
                                  }
                                  data-testid={`preload-invite-${r.slug}`}
                                >
                                  <PaperPlaneTilt weight="bold" className="h-3.5 w-3.5" />
                                  {sendingInviteId === r.id
                                    ? "Sending…"
                                    : r.claim_invite_sent_at
                                    ? "Resend"
                                    : "Invite"}
                                </button>
                              )}
                              <button
                                type="button"
                                onClick={() => openEdit(r)}
                                className="inline-flex items-center gap-1 text-primary hover:underline mr-2"
                                data-testid={`preload-edit-${r.slug}`}
                              >
                                <PencilSimple weight="bold" className="h-3.5 w-3.5" /> Edit
                              </button>
                            </>
                          )}
                          <a
                            href={profileHref}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-primary hover:underline"
                            data-testid={`preload-view-${r.slug}`}
                          >
                            <Eye weight="bold" className="h-3.5 w-3.5" /> Profile
                          </a>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* 7. Edit drawer modal */}
      {editing && (
        <EditDrawer
          row={editing}
          form={editForm}
          setForm={setEditForm}
          onClose={() => setEditing(null)}
          onSave={handleSaveEdit}
          saving={saving}
        />
      )}

      {/* 8. Bulk archive / delete confirmation modal */}
      {bulkOpen && (
        <BulkConfirmModal
          city={activeCity}
          mode={bulkArchiveMode}
          setMode={setBulkArchiveMode}
          confirmText={bulkConfirmText}
          setConfirmText={setBulkConfirmText}
          onCancel={() => setBulkOpen(false)}
          onConfirm={handleBulkSubmit}
          running={bulkRunning}
        />
      )}

      {/* 9. Bulk-invite confirmation modal — shows a live dry-run
              count so the admin knows exactly how many emails will
              fire, with a toggle for the resend-to-already-invited case. */}
      {bulkInviteOpen && (
        <BulkInviteModal
          city={activeCity}
          preview={bulkInvitePreview}
          previewLoading={bulkInvitePreviewLoading}
          resend={bulkInviteResend}
          onResendToggle={handleBulkInviteResendToggle}
          onCancel={() => { setBulkInviteOpen(false); setBulkInviteResult(null); }}
          onConfirm={handleBulkInviteSubmit}
          running={bulkInviteRunning}
          result={bulkInviteResult}
        />
      )}
    </div>
  );
}

function DashTile({ label, value, subtle, testId }) {
  return (
    <div
      className={`rounded-lg px-3 py-2 ${subtle ? "bg-amber-50 border border-amber-200" : "bg-card border border-border"}`}
      data-testid={testId}
    >
      <div className={`text-xl font-bold ${subtle ? "text-amber-800" : "text-foreground"}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</div>
    </div>
  );
}

function PreviewPanel({ report, onConfirm, onCancel, onDownloadErrors }) {
  const inserts = (report.preview || []).filter(p => p.action === "insert");
  const updates = (report.preview || []).filter(p => p.action === "update");
  return (
    <div className="rounded-lg border-2 border-primary/40 bg-primary/5 p-4 space-y-3" data-testid="preload-preview-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="font-bold text-sm inline-flex items-center gap-1.5">
          <Eye weight="duotone" className="h-4 w-4 text-primary" />
          Preview · nothing has been saved yet
        </div>
        <div className="text-xs text-muted-foreground">city: <span className="font-bold text-foreground">{report.city_slug}</span></div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
        <DashTile label="New trucks" value={inserts.length} testId="preview-new" />
        <DashTile label="To update" value={updates.length} testId="preview-update" />
        <DashTile label="Skipped" value={report.skipped || 0} subtle testId="preview-skipped" />
        <DashTile label="Errors" value={(report.errors || []).length} subtle testId="preview-errors" />
      </div>

      {(report.errors || []).length > 0 && (
        <div className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded p-2 space-y-1">
          <div className="font-bold inline-flex items-center justify-between w-full">
            <span className="inline-flex items-center gap-1.5">
              <WarningCircle weight="fill" className="h-4 w-4 text-amber-600" />
              {report.errors.length} row error{report.errors.length === 1 ? "" : "s"} will be skipped
            </span>
            <Button type="button" size="sm" variant="ghost" onClick={onDownloadErrors} data-testid="preview-download-errors">
              <DownloadSimple weight="bold" className="h-3.5 w-3.5 mr-1" />
              errors.csv
            </Button>
          </div>
          <ul className="list-disc pl-5 max-h-32 overflow-y-auto">
            {report.errors.slice(0, 20).map((e, i) => (
              <li key={i}>Row {e.row}: {e.reason}</li>
            ))}
          </ul>
        </div>
      )}

      <details className="text-xs">
        <summary className="cursor-pointer text-muted-foreground hover:text-foreground select-none">
          Show {(report.preview || []).length} row preview
        </summary>
        <div className="max-h-48 overflow-y-auto mt-2 rounded border border-border">
          <table className="w-full text-xs">
            <thead className="bg-muted/40 sticky top-0">
              <tr className="text-left">
                <th className="px-2 py-1">#</th>
                <th className="px-2 py-1">Action</th>
                <th className="px-2 py-1">Truck</th>
                <th className="px-2 py-1">Slug</th>
                <th className="px-2 py-1">Tier</th>
                <th className="px-2 py-1">Research</th>
              </tr>
            </thead>
            <tbody>
              {(report.preview || []).map((p, i) => (
                <tr key={i} className="border-t border-border" data-testid={`preview-row-${p.slug}`}>
                  <td className="px-2 py-1">{p.row}</td>
                  <td className="px-2 py-1">
                    <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${p.action === "insert" ? "bg-emerald-100 text-emerald-800" : "bg-blue-100 text-blue-800"}`}>
                      {p.action}
                    </span>
                  </td>
                  <td className="px-2 py-1 font-medium">{p.truck_name}</td>
                  <td className="px-2 py-1 font-mono">{p.slug}</td>
                  <td className="px-2 py-1">{p.priority_tier || "—"}</td>
                  <td className="px-2 py-1">{RESEARCH_LABELS[p.research_status] || p.research_status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>

      <div className="flex gap-2 pt-2">
        <Button
          onClick={onConfirm}
          disabled={inserts.length === 0 && updates.length === 0}
          className="flex-1"
          data-testid="preload-confirm-btn"
        >
          <CheckCircle weight="bold" className="h-4 w-4 mr-1.5" />
          Confirm import ({inserts.length} new + {updates.length} updates)
        </Button>
        <Button type="button" variant="outline" onClick={onCancel} data-testid="preload-cancel-btn">
          Cancel
        </Button>
      </div>
    </div>
  );
}

function EditDrawer({ row, form, setForm, onClose, onSave, saving }) {
  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" onClick={onClose} data-testid="preload-edit-drawer">
      <div
        className="bg-background w-full max-w-xl h-full overflow-y-auto p-6 space-y-4 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Edit preloaded truck</div>
            <h3 className="text-lg font-bold">{row.truck_name}</h3>
            <div className="text-xs text-muted-foreground font-mono">{row.city_slug} / {row.slug}</div>
          </div>
          <Button type="button" variant="ghost" size="icon" onClick={onClose} data-testid="preload-edit-close">
            <X weight="bold" className="h-5 w-5" />
          </Button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Truck name"><Input value={form.truck_name} onChange={update("truck_name")} data-testid="edit-truck-name" /></Field>
          <Field label="Cuisine"><Input value={form.cuisine_type} onChange={update("cuisine_type")} data-testid="edit-cuisine" /></Field>
          <Field label="Priority tier">
            <select value={form.priority_tier} onChange={update("priority_tier")} className="h-10 rounded-md border border-input bg-background px-3 text-sm w-full" data-testid="edit-tier">
              {PRIORITY_OPTIONS.map(o => <option key={o} value={o}>{o || "—"}</option>)}
            </select>
          </Field>
          <Field label="Opportunity score">
            <Input type="number" step="0.1" value={form.opportunity_score} onChange={update("opportunity_score")} data-testid="edit-score" />
          </Field>
          <Field label="Status">
            <select value={form.status} onChange={update("status")} className="h-10 rounded-md border border-input bg-background px-3 text-sm w-full" data-testid="edit-status">
              {STATUS_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </Field>
          <Field label="Claim status">
            <select value={form.claim_status} onChange={update("claim_status")} className="h-10 rounded-md border border-input bg-background px-3 text-sm w-full" data-testid="edit-claim-status">
              {CLAIM_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </Field>
          <Field label="Research status">
            <select value={form.research_status} onChange={update("research_status")} className="h-10 rounded-md border border-input bg-background px-3 text-sm w-full" data-testid="edit-research-status">
              {RESEARCH_OPTIONS.map(o => <option key={o} value={o}>{RESEARCH_LABELS[o]}</option>)}
            </select>
          </Field>
          <Field label="Email (CSV-on-file)"><Input value={form.email} onChange={update("email")} data-testid="edit-email" /></Field>
          <Field label="Phone"><Input value={form.phone} onChange={update("phone")} data-testid="edit-phone" /></Field>
          <Field label="Website"><Input value={form.website_url} onChange={update("website_url")} data-testid="edit-website" /></Field>
          <Field label="Instagram"><Input value={form.instagram_url} onChange={update("instagram_url")} data-testid="edit-instagram" /></Field>
          <Field label="Facebook"><Input value={form.facebook_url} onChange={update("facebook_url")} data-testid="edit-facebook" /></Field>
          <Field label="TikTok"><Input value={form.tiktok_url} onChange={update("tiktok_url")} data-testid="edit-tiktok" /></Field>
          <Field label="Google Maps"><Input value={form.google_maps_url} onChange={update("google_maps_url")} data-testid="edit-gmaps" /></Field>
          <Field label="Yelp"><Input value={form.yelp_url} onChange={update("yelp_url")} data-testid="edit-yelp" /></Field>
          <Field label="Food truck park"><Input value={form.food_truck_park} onChange={update("food_truck_park")} data-testid="edit-park" /></Field>
          <Field label="Service area"><Input value={form.service_area} onChange={update("service_area")} data-testid="edit-service-area" /></Field>
          <Field label="Logo URL"><Input value={form.logo_url} onChange={update("logo_url")} data-testid="edit-logo" /></Field>
        </div>
        <Field label="Short description">
          <Textarea rows={3} value={form.short_description} onChange={update("short_description")} data-testid="edit-description" />
        </Field>
        <Field label="Source notes (admin-only)">
          <Textarea rows={2} value={form.source_notes} onChange={update("source_notes")} data-testid="edit-source-notes" />
        </Field>

        <div className="flex gap-2 pt-4 border-t border-border sticky bottom-0 bg-background pb-1">
          <Button onClick={onSave} disabled={saving} className="flex-1" data-testid="preload-edit-save">
            <FloppyDisk weight="bold" className="h-4 w-4 mr-1.5" />
            {saving ? "Saving…" : "Save changes"}
          </Button>
          <Button variant="outline" onClick={onClose} data-testid="preload-edit-cancel">Cancel</Button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground block mb-1">{label}</span>
      {children}
    </label>
  );
}

function ExportMenu({ onExport }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => setOpen((v) => !v)}
        data-testid="preload-export-btn"
      >
        <DownloadSimple weight="bold" className="h-3.5 w-3.5 mr-1" />
        Export CSV
      </Button>
      {open && (
        <div
          className="absolute right-0 top-full mt-1 z-20 bg-popover border border-border rounded-md shadow-lg min-w-[180px] py-1"
          onMouseLeave={() => setOpen(false)}
          data-testid="preload-export-menu"
        >
          {[
            ["all", "Whole city"],
            ["claimed", "Claimed only"],
            ["unclaimed", "Unclaimed only"],
            ["verified", "Verified only"],
            ["pending", "Pending review"],
          ].map(([k, label]) => (
            <button
              key={k}
              type="button"
              className="block w-full text-left px-3 py-2 text-xs hover:bg-muted"
              onClick={() => { setOpen(false); onExport(k); }}
              data-testid={`preload-export-${k}`}
            >
              {label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function BulkConfirmModal({ city, mode, setMode, confirmText, setConfirmText, onCancel, onConfirm, running }) {
  const isHard = !mode;
  const matches = confirmText.trim().toLowerCase() === city;
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onCancel} data-testid="preload-bulk-modal">
      <div
        className="bg-background rounded-lg max-w-md w-full p-6 space-y-4 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3">
          <div className={`rounded-full p-2 ${isHard ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>
            {isHard ? <Trash weight="bold" className="h-5 w-5" /> : <Archive weight="bold" className="h-5 w-5" />}
          </div>
          <div className="flex-1">
            <h3 className="font-bold text-base">
              {isHard ? "Hard delete" : "Archive"} all preloaded trucks for &quot;{city}&quot;?
            </h3>
            <p className="text-xs text-muted-foreground mt-1">
              {isHard
                ? "Rows will be permanently removed from the database. No undo. Use for testing-only cleanup."
                : "Rows will be hidden from public list/stats but stay in the DB. You can restore individual rows from the table after enabling 'Show archived'."}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3 text-xs">
          <label className="inline-flex items-center gap-1.5 cursor-pointer">
            <input type="radio" checked={mode === true} onChange={() => setMode(true)} data-testid="bulk-mode-archive" />
            <span className="font-semibold">Archive (safe, recoverable)</span>
          </label>
          <label className="inline-flex items-center gap-1.5 cursor-pointer">
            <input type="radio" checked={mode === false} onChange={() => setMode(false)} data-testid="bulk-mode-delete" />
            <span className="font-semibold text-red-700">Hard delete (gone forever)</span>
          </label>
        </div>

        <div>
          <Label htmlFor="bulk-confirm" className="text-xs">
            Type &quot;<span className="font-bold">{city}</span>&quot; to confirm
          </Label>
          <Input
            id="bulk-confirm"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder={city}
            autoComplete="off"
            data-testid="bulk-confirm-input"
          />
        </div>

        <div className="flex gap-2 pt-2">
          <Button
            onClick={onConfirm}
            disabled={!matches || running}
            variant={isHard ? "destructive" : "default"}
            className="flex-1"
            data-testid="bulk-confirm-btn"
          >
            {running ? "Working…" : (isHard ? "Delete forever" : "Archive all")}
          </Button>
          <Button type="button" variant="outline" onClick={onCancel} data-testid="bulk-cancel-btn">Cancel</Button>
        </div>
      </div>
    </div>
  );
}

function BulkInviteModal({
  city,
  preview,
  previewLoading,
  resend,
  onResendToggle,
  onCancel,
  onConfirm,
  running,
  result,
}) {
  const eligible = preview?.eligible ?? 0;
  const cap = preview?.max_per_call ?? 200;
  const overCap = eligible > cap;
  const cityLabel = (city || "").charAt(0).toUpperCase() + (city || "").slice(1);
  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={onCancel}
      data-testid="preload-bulk-invite-modal"
    >
      <div
        className="bg-background rounded-lg max-w-md w-full p-6 space-y-4 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3">
          <div className="rounded-full p-2 bg-primary/10 text-primary">
            <PaperPlaneTilt weight="bold" className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <h3 className="font-bold text-base">
              Send claim invites to {cityLabel} trucks?
            </h3>
            <p className="text-xs text-muted-foreground mt-1">
              Fires the city-aware invite email to every preloaded truck with
              an email on file that isn&rsquo;t archived, isn&rsquo;t already
              verified-claimed, and{" "}
              {resend
                ? "regardless of whether they've been invited before."
                : "hasn't been invited yet."}
            </p>
          </div>
        </div>

        {result ? (
          <div
            className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 space-y-1 text-xs"
            data-testid="bulk-invite-result"
          >
            <div className="font-semibold text-emerald-900">
              Sent {result.sent} of {result.attempted} attempted
              {result.failed > 0 && (
                <span className="text-amber-800"> · {result.failed} failed</span>
              )}
            </div>
            {result.remaining > 0 && (
              <div className="text-muted-foreground">
                {result.remaining} more truck{result.remaining === 1 ? "" : "s"} still
                eligible — click again to send the next batch (cap is{" "}
                {result.batch_cap}).
              </div>
            )}
            {result.failed_rows && result.failed_rows.length > 0 && (
              <details className="pt-1">
                <summary className="cursor-pointer text-amber-900 font-semibold">
                  Show failed rows
                </summary>
                <ul className="list-disc pl-5 pt-1 max-h-32 overflow-y-auto">
                  {result.failed_rows.map((r) => (
                    <li key={r.id}>
                      {r.truck_name} — {r.email}
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        ) : (
          <div className="rounded-lg border border-border bg-muted/30 p-3 space-y-2">
            <div className="flex items-baseline justify-between">
              <div className="text-2xl font-black tabular-nums text-primary">
                {previewLoading ? "…" : eligible}
              </div>
              <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">
                Trucks to email
              </div>
            </div>
            {overCap && (
              <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                Batch cap is {cap} — this click will send to the top {cap} by
                priority/opportunity, and you can click again to catch the rest.
              </div>
            )}
            {preview?.sample?.length > 0 && (
              <details className="text-xs">
                <summary className="cursor-pointer text-muted-foreground hover:text-foreground select-none">
                  Preview {Math.min(preview.sample.length, 5)} of {eligible}
                </summary>
                <ul className="mt-1 space-y-0.5 pl-4 list-disc text-muted-foreground">
                  {preview.sample.map((r, i) => (
                    <li key={i}>
                      <span className="text-foreground font-medium">{r.truck_name}</span>{" "}
                      · {r.email}
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}

        {!result && (
          <label className="inline-flex items-center gap-2 text-xs cursor-pointer">
            <input
              type="checkbox"
              checked={resend}
              onChange={(e) => onResendToggle(e.target.checked)}
              data-testid="bulk-invite-resend-toggle"
              disabled={running || previewLoading}
            />
            <span>
              Also re-invite trucks that have already been invited before
            </span>
          </label>
        )}

        <div className="flex gap-2 pt-1">
          {result ? (
            <Button
              type="button"
              onClick={onCancel}
              className="flex-1"
              data-testid="bulk-invite-close-btn"
            >
              Close
            </Button>
          ) : (
            <>
              <Button
                onClick={onConfirm}
                disabled={!eligible || running || previewLoading}
                className="flex-1"
                data-testid="bulk-invite-confirm-btn"
              >
                <PaperPlaneTilt weight="bold" className="h-4 w-4 mr-1.5" />
                {running
                  ? "Sending…"
                  : `Send ${Math.min(eligible, cap)} invite${Math.min(eligible, cap) === 1 ? "" : "s"}`}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={onCancel}
                data-testid="bulk-invite-cancel-btn"
              >
                Cancel
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

